import type { operations } from "@/openapi.gen.ts";
import { isTerminalRunStatus } from "@/shared/agent-run-status.ts";
import { $api } from "@/utils/fetch.ts";
import {
	type GetNextPageParamFunction,
	type QueryClient,
	type QueryKey,
	useQueryClient,
} from "@tanstack/react-query";

/**
 * Lifecycle of a headless run. Derived from the generated API rather than
 * re-listing the backend's `AGENT_RUN_STATUSES`, so a new status reaches the
 * frontend as a type error instead of an unhandled badge.
 */
export type AgentRunStatus =
	operations["api_agent-definitions_agentId_runs_runId_get"]["responses"][200]["content"]["application/json"]["status"];

/**
 * What started a run. Derived from the generated API for the same reason as
 * {@link AgentRunStatus}: a new origin reaches the frontend as a type error on
 * the label map rather than as an unlabelled badge.
 */
export type AgentRunOrigin =
	operations["api_agent-definitions_agentId_runs_runId_get"]["responses"][200]["content"]["application/json"]["origin"];

/** A run whose status can still change, so its detail/list keeps polling. */
export const isRunNonTerminal = (status: AgentRunStatus): boolean =>
	!isTerminalRunStatus(status);

/** How often the runs surfaces re-poll while a run is not yet terminal. */
const RUN_POLL_INTERVAL_MS = 15000;

interface AgentRunListPage {
	runs: { status: AgentRunStatus }[];
	totalCount: number;
	hasNextPage?: boolean;
}

/**
 * An agent's runs, newest first. Mirrors `useAgentDefinitionsInfiniteQuery`, and
 * polls at 15 s (the `agents-service.ts` pattern) so a run flipping status
 * out-of-band — the engine claims it, or a parked run resumes — surfaces without
 * a websocket or a manual refresh.
 */
export const useAgentRunsInfiniteQuery = (
	agentId: string,
	pageSize?: number,
) => {
	const getNextPageParam: GetNextPageParamFunction<
		unknown,
		AgentRunListPage
	> = (lastPage, _allPages, lastPageParam) => {
		if (lastPage.hasNextPage && typeof lastPageParam === "number") {
			return lastPageParam + 1;
		}
		return undefined;
	};

	return $api.useInfiniteQuery(
		"get",
		"/api/agent-definitions/{agentId}/runs",
		{ params: { path: { agentId }, query: { pageSize } } },
		{
			pageParamName: "page",
			initialPageParam: 0,
			getNextPageParam,
			refetchInterval: RUN_POLL_INTERVAL_MS,
		},
	);
};

/**
 * One run's detail. Polls while the run is non-terminal (so a `waiting`
 * → `succeeded` transition driven by the resume worker appears), and stops once
 * the run reaches a terminal status — nothing more will change.
 */
export const agentRunQueryOptions = (agentId: string, runId: string) =>
	$api.queryOptions(
		"get",
		"/api/agent-definitions/{agentId}/runs/{runId}",
		{ params: { path: { agentId, runId } } },
		{
			refetchInterval: (query) => {
				const status = query.state.data?.status as AgentRunStatus | undefined;
				return status && isRunNonTerminal(status)
					? RUN_POLL_INTERVAL_MS
					: false;
			},
		},
	);

/**
 * A cheap per-agent count of runs waiting for approval, for the
 * "a run is awaiting approval" badge on the agents overview. Reuses the runs
 * list endpoint with a status filter and `pageSize: 1` — only
 * `awaitingApprovalCount` is read (`totalCount` counts the waiting runs a page
 * would return, including ones already approved and resuming), the single
 * returned run is ignored. Polls like the other runs surfaces
 * so a run that parks out-of-band surfaces without a manual refresh, and shares
 * the `.../runs` literal path so a "run now" / approve invalidation refreshes it.
 */
export const agentWaitingRunsCountQueryOptions = (agentId: string) =>
	$api.queryOptions(
		"get",
		"/api/agent-definitions/{agentId}/runs",
		{
			params: {
				path: { agentId },
				query: { status: "waiting", pageSize: 1 },
			},
		},
		{ refetchInterval: RUN_POLL_INTERVAL_MS },
	);

/**
 * The caller's approval queue: every run waiting for approval that
 * this user may approve, across every agent.
 *
 * Why this exists next to the notification bell rather than inside it: a
 * notification is a feed item and a pending approval is durable state. The bell
 * counts UNREAD rows, so reading the notification cleared the task from view
 * while the run stayed parked. This counts the runs themselves, so it only drops
 * when the work is actually decided. The two are complementary and both stay.
 *
 * Polls like every other runs surface (the `agentWaitingRunsCountQueryOptions`
 * neighbour): a run parks, resumes or is decided by a colleague out-of-band, and
 * none of those reach this client any other way.
 */
export const useApprovalQueueInfiniteQuery = (pageSize?: number) => {
	const getNextPageParam: GetNextPageParamFunction<
		unknown,
		{ hasNextPage?: boolean }
	> = (lastPage, _allPages, lastPageParam) => {
		if (lastPage.hasNextPage && typeof lastPageParam === "number") {
			return lastPageParam + 1;
		}
		return undefined;
	};

	return $api.useInfiniteQuery(
		"get",
		"/api/agent-definitions/approvals",
		{ params: { query: { pageSize } } },
		{
			pageParamName: "page",
			initialPageParam: 0,
			getNextPageParam,
			refetchInterval: RUN_POLL_INTERVAL_MS,
		},
	);
};

/**
 * The outstanding-approvals number for the nav badge. Same endpoint as the queue
 * itself with `pageSize: 1` — only `awaitingApprovalCount` is read, so the badge
 * and the queue are computed from ONE server-side scope and cannot disagree about
 * WHICH runs count (the notification bell's "one query serves both" property).
 * They differ by one deliberate thing: a run already approved and waiting on the
 * resume worker is still LISTED (flagged `resuming`) but not COUNTED, so the
 * badge never asks for a decision that has just been made.
 */
export const approvalQueueCountQueryOptions = () =>
	$api.queryOptions(
		"get",
		"/api/agent-definitions/approvals",
		{ params: { query: { pageSize: 1 } } },
		{ refetchInterval: RUN_POLL_INTERVAL_MS },
	);

/**
 * The pending confirmable tool call(s) of a run waiting for approval
 * — the source of the `callId` the approve mutation needs. Server-gated on the
 * approver rule: a non-approver gets a 403, which the panel reflects.
 */
export const agentRunPendingQueryOptions = (agentId: string, runId: string) =>
	$api.queryOptions(
		"get",
		"/api/agent-definitions/{agentId}/runs/{runId}/pending",
		{ params: { path: { agentId, runId } } },
	);

// openapi-react-query keys every query as [method, literalPath, init]. This
// prefix matches the runs list and its infinite variant (both share the literal
// ".../runs" path); partialMatchKey compares element 2 by exact string, so it
// does NOT match the detail (".../runs/{runId}") or pending
// (".../runs/{runId}/pending") queries — those must be invalidated by their own
// keys (see useApproveAgentRunMutation).
/**
 * The cross-agent approval queue, as a two-element prefix: both its paged list
 * and the badge's `pageSize: 1` variant share the literal path and differ only in
 * element 3, which a partial match ignores. Deciding a run must leave both stale.
 */
export const APPROVAL_QUEUE_QUERY_PREFIX = [
	"get",
	"/api/agent-definitions/approvals",
];

export const runsQueryPrefix = (agentId: string): QueryKey => [
	"get",
	"/api/agent-definitions/{agentId}/runs",
	{ params: { path: { agentId } } },
];

export const useCreateAgentRunMutation = (agentId: string) => {
	const queryClient = useQueryClient();
	return $api.useMutation("post", "/api/agent-definitions/{agentId}/runs", {
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: runsQueryPrefix(agentId) });
		},
	});
};

/**
 * Every query a verdict on one parked call leaves stale. Four literal paths, and
 * each one needs its own key because `partialMatchKey` compares element 2 by
 * exact string:
 *
 *  - the agent's runs list and its infinite variant (one prefix covers both, and
 *    the per-agent waiting badge, which is that same path with a status filter);
 *  - the run's detail, whose status flips once the worker resumes;
 *  - the run's pending calls — without it, a turn that parked several calls keeps
 *    showing an already-decided one with live buttons;
 *  - the cross-agent approval queue and its badge, which share the `/approvals`
 *    path and differ only in element 3.
 *
 * Exported as a list rather than inlined so the set is pinned by a test against a
 * real QueryClient (`agent-invalidation.test.ts`) — the same treatment the agent
 * definition keys got after a "simplified" prefix reached neither the detail nor
 * the schedules query.
 */
export const agentRunVerdictInvalidationKeys = (
	agentId: string,
	runId: string,
): QueryKey[] => [
	runsQueryPrefix(agentId),
	agentRunQueryOptions(agentId, runId).queryKey,
	agentRunPendingQueryOptions(agentId, runId).queryKey,
	APPROVAL_QUEUE_QUERY_PREFIX,
];

const invalidateRunQueries = (
	queryClient: QueryClient,
	agentId: string,
	runId: string,
): void => {
	for (const queryKey of agentRunVerdictInvalidationKeys(agentId, runId)) {
		queryClient.invalidateQueries({ queryKey });
	}
};

/**
 * Approve or reject one parked tool call (the C4 write path). Once it settles,
 * every surface that could show a stale verdict is invalidated — see
 * {@link agentRunVerdictInvalidationKeys}. On settle rather than on success: a
 * 409 means the call is no longer waiting (someone else decided it first), and
 * the pending list does not poll, so only a refetch takes its buttons away.
 */
export const useApproveAgentRunMutation = (agentId: string) => {
	const queryClient = useQueryClient();
	return $api.useMutation(
		"post",
		"/api/agent-definitions/{agentId}/runs/{runId}/confirm",
		{
			onSettled: (_data, _error, variables) => {
				invalidateRunQueries(queryClient, agentId, variables.params.path.runId);
			},
		},
	);
};

/**
 * Cancel a run. Leaves stale the same queries a verdict does: the run's status
 * changes, a waiting run leaves the approval queue, and its pending calls go.
 */
export const useCancelAgentRunMutation = (agentId: string) => {
	const queryClient = useQueryClient();
	return $api.useMutation(
		"post",
		"/api/agent-definitions/{agentId}/runs/{runId}/cancel",
		{
			onSettled: (_data, _error, variables) => {
				invalidateRunQueries(queryClient, agentId, variables.params.path.runId);
			},
		},
	);
};

/**
 * Retry a failed or cancelled run. The new run lands in the runs list, and the
 * original's detail gains its "retried as" link.
 */
export const useRetryAgentRunMutation = (agentId: string) => {
	const queryClient = useQueryClient();
	return $api.useMutation(
		"post",
		"/api/agent-definitions/{agentId}/runs/{runId}/retry",
		{
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({ queryKey: runsQueryPrefix(agentId) });
				queryClient.invalidateQueries({
					queryKey: agentRunQueryOptions(agentId, variables.params.path.runId)
						.queryKey,
				});
			},
		},
	);
};
